import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { userDataApi } from "@/api/user-data.api";
import { useApp } from "@/hooks/use-app-context";
import { useToast } from "@/hooks/use-toast";
import { buildProfilePayload, emptyForm, extractErrorMessage, validateProfileForm, type FormProblem, type ProfileForm } from "./profile-form";
import type { SettingsCopy } from "./settings-copy";

export type SettingsSheet = "profile" | "languages" | "password" | "privacy" | null;

/**
 * State and actions of the settings page. The API calls and payloads are the
 * ones the page always made; only the way they are reached changed.
 */
export function useSettings(copy: SettingsCopy) {
  const { t, user, language, setUser } = useApp();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [sheet, setSheet] = useState<SettingsSheet>(null);
  const [form, setForm] = useState<ProfileForm>(() => emptyForm(user));
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deletePasswordError, setDeletePasswordError] = useState<string | null>(null);

  // Each sheet starts from the stored profile; nothing typed survives a cancel.
  useEffect(() => {
    if (user && sheet === null) setForm(emptyForm(user));
  }, [user, sheet]);

  const fail = useCallback(
    (description: string) => toast({ title: t.error, description, variant: "destructive" }),
    [t, toast],
  );

  const problemText: Record<FormProblem, string> = {
    usernameEmpty: t.settingsUsernameEmpty,
    languagesRequired: t.languagePreferencesRequired,
    passwordRequired: t.settingsPasswordRequired,
    passwordMismatch: t.settingsPasswordMismatch,
    newPasswordEmpty: copy.newPasswordEmpty,
  };

  const save = async () => {
    if (!user) return;
    const problem = validateProfileForm(form, user.role, { requirePassword: sheet === "password" });
    if (problem) {
      fail(problemText[problem]);
      return;
    }
    setIsSaving(true);
    try {
      const dto = buildProfilePayload(form);
      await userDataApi.updateUser(user.username || user.id, dto);
      setUser({
        ...user,
        ...dto,
        email: dto.email === null ? undefined : dto.email ?? user.email,
        phoneNumber: dto.phoneNumber === null ? undefined : dto.phoneNumber ?? user.phoneNumber,
      });
      toast({ title: t.success, description: t.settingsProfileUpdated });
      setSheet(null);
    } catch (error) {
      fail(extractErrorMessage(error) || t.settingsUpdateError);
    } finally {
      setIsSaving(false);
    }
  };

  // Copy built on the spot: emailed when the account has an address,
  // otherwise saved straight away by the browser.
  const requestExport = async () => {
    if (!user?.id) return;
    setIsDownloading(true);
    try {
      const response = await userDataApi.requestDataExport({ userId: Number(user.id), requestType: "full" });
      if (response.delivery === "email") {
        toast({ title: t.success, description: t.settingsDataExportEmailed.replace("{email}", response.email ?? "") });
      } else {
        await userDataApi.downloadDataExport();
        toast({ title: t.success, description: t.settingsDataExportDownloaded });
      }
    } catch (error) {
      fail(extractErrorMessage(error) ?? t.settingsDataExportError);
    } finally {
      setIsDownloading(false);
    }
  };

  // The deletion dialog: nothing typed survives a close.
  const setDeletionOpen = (open: boolean) => {
    if (isDeleting) return;
    setDeleteOpen(open);
    if (!open) {
      setDeletePassword("");
      setDeletePasswordError(null);
    }
  };

  const changeDeletePassword = (value: string) => {
    setDeletePassword(value);
    setDeletePasswordError(null);
  };

  // Immediate deletion once the password is confirmed; the account is then
  // anonymised server side and this session ends. A wrong password (403
  // WRONG_PASSWORD) stays in the dialog, without signing out.
  const confirmDeletion = async () => {
    if (!user?.id || !deletePassword) return;
    setIsDeleting(true);
    setDeletePasswordError(null);
    try {
      await userDataApi.requestAccountDeletion({ userId: Number(user.id), password: deletePassword });
      setDeleteOpen(false);
      setDeletePassword("");
      toast({ title: t.success, description: t.settingsAccountDeleted });
      setUser(null);
      navigate(`/${language}/login`, { replace: true });
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 403 && (extractErrorMessage(error) ?? "").includes("WRONG_PASSWORD")) {
        setDeletePasswordError(t.settingsWrongPassword);
      } else {
        fail(extractErrorMessage(error) ?? t.settingsDeleteProcessError);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const openSheet = (next: SettingsSheet) => {
    setForm(emptyForm(user));
    setSheet(next);
  };

  const closeSheet = () => {
    if (!isSaving) setSheet(null);
  };

  const patch = (fields: Partial<ProfileForm>) => setForm((current) => ({ ...current, ...fields }));

  return {
    sheet,
    openSheet,
    closeSheet,
    form,
    patch,
    save,
    isSaving,
    requestExport,
    isDownloading,
    isDeleting,
    deletion: {
      open: deleteOpen,
      setOpen: setDeletionOpen,
      password: deletePassword,
      setPassword: changeDeletePassword,
      passwordError: deletePasswordError,
      confirm: confirmDeletion,
    },
  };
}

export type SettingsState = ReturnType<typeof useSettings>;
